import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fileToBase64 } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';
import { DELIVERY_STATUS, eur, monthKey, period, RECONCILIATION_STATUS, STATEMENT_STATUS, usd, VOUCHER_STATUS, type DeliveryStatus, type StatementStatus, type VoucherStatus } from '../format.js';

interface Month {
  year: number;
  month: number;
}
interface Candidate extends Month {
  status: 'matched' | 'mismatch' | 'report_missing' | 'overridden';
  revenueShareUsd: string;
  source: 'report' | 'api' | 'none';
  beforeLastSettled: boolean;
}
interface Proposal {
  status: 'matched' | 'manual';
  months: Month[];
  totalUsd: string | null;
  candidates: Candidate[];
  selected: Month[];
  blocker: { error: string; message: string } | null;
  preview: {
    totalUsd: string;
    netEur: string;
    costsEur: string;
    statements: Array<{ partnerId: string; partnerName: string; isHouse: boolean; partnerRevenueUsd: string; sharePercent: string; payoutEur: string }>;
  } | null;
}
export interface PaymentStatement {
  id: string;
  partnerName: string;
  isHouse: boolean;
  number: string | null;
  status: StatementStatus;
  payoutEur: string;
  partnerRevenueUsd: string;
  reversesStatementId: string | null;
  deliveryStatus?: DeliveryStatus | null;
}
export interface Payment {
  id: string;
  paidOn: string | null;
  amountEur: string;
  amountUsd: string | null;
  costsEur: string;
  coveredFrom: string;
  coveredTo: string;
  months: Month[];
  reversedAt: string | null;
  reversalReason: string | null;
  legacyPaymentId: number | null;
  statements: PaymentStatement[];
  /** Valve's payment notice (PDF), attached to the payment's Lexware voucher (STE-11). */
  notice?: { id: string; filename: string; sizeBytes: number; createdAt: string } | null;
  /** The Sparkasse transaction detail the payment was entered from: the payment's evidence for the voucher (STE-24). */
  bankDocument?: { id: string; filename: string; sizeBytes: number; createdAt: string } | null;
  bankReference?: string | null;
  voucher?: { id: string; status: VoucherStatus; voucherNumber: string; voucherId: string | null; lastError: string | null; paidOn: string | null } | null;
}

/** Payment notice upload and the Lexware state of the payment's voucher. */
export function PaymentBookkeeping({ payment, onChanged }: { payment: Payment; onChanged: () => Promise<unknown> }) {
  const isAdmin = useMe().user?.role === 'admin';
  const [file, setFile] = useState<File | null>(null);
  const upload = useMutation({
    mutationFn: async () => api('PUT', `/api/payments/${payment.id}/notice`, { filename: file!.name, contentBase64: await fileToBase64(file!) }),
    onSuccess: async () => {
      setFile(null);
      await onChanged();
    },
  });
  const locked = payment.voucher && !['pending', 'failed'].includes(payment.voucher.status);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
      {payment.bankDocument && (
        <span>
          Bank transaction: <a className="underline" href={`/api/payments/${payment.id}/bank-document`}>{payment.bankDocument.filename}</a>
        </span>
      )}
      <span>
        Payment notice:{' '}
        {payment.notice ? (
          <a className="underline" href={`/api/payments/${payment.id}/notice`}>{payment.notice.filename}</a>
        ) : payment.bankDocument ? (
          <span className="text-slate-500">none (the bank transaction is the evidence)</span>
        ) : (
          <span className="text-amber-700 dark:text-amber-400">missing</span>
        )}
      </span>
      <span>
        Lexware:{' '}
        {payment.voucher ? (
          <span className={VOUCHER_STATUS[payment.voucher.status].className}>
            {VOUCHER_STATUS[payment.voucher.status].label} {payment.voucher.voucherNumber}{payment.voucher.lastError ? `: ${payment.voucher.lastError}` : ''}
          </span>
        ) : (
          <span className="text-slate-500">not booked</span>
        )}
      </span>
      {isAdmin && !payment.reversedAt && !locked && (
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); upload.mutate(); }}>
          <input type="file" accept="application/pdf,.pdf" aria-label={`Payment notice ${payment.paidOn ?? ''}`} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <Button type="submit" disabled={!file || upload.isPending}>{payment.notice ? 'Replace notice' : 'Upload notice'}</Button>
        </form>
      )}
      {upload.isError && <ErrorText>{errorText(upload.error, 'The notice could not be stored')}</ErrorText>}
    </div>
  );
}

interface BankPrefill {
  paidOn: string;
  amountEur: string;
  amountUsd: string;
  rate: string | null;
  intermediaryFeeUsd: string | null;
  bankReference: string;
  costsEur: string;
}

const RECONCILIATION = RECONCILIATION_STATUS;
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.message ?? e.code) : fallback);
const cents = (v: string) => Math.round(Number(v) * 100);

function NewPayment({ onCreated }: { onCreated: () => Promise<unknown> }) {
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api<{ paymentCostsEur: string }>('GET', '/api/settings') });
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [amountEur, setAmountEur] = useState('');
  const [amountUsd, setAmountUsd] = useState('');
  const [costsEur, setCostsEur] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number> | null>(null);
  const [acceptDifference, setAcceptDifference] = useState(false);
  const [bank, setBank] = useState<{ filename: string; html: string; reference: string } | null>(null);
  const [bankInfo, setBankInfo] = useState<BankPrefill | null>(null);
  const [pasted, setPasted] = useState('');
  const costs = costsEur ?? settings.data?.paymentCostsEur ?? '';

  const months = selected ? [...selected].sort((a, b) => a - b).map((k) => ({ year: Math.trunc(k / 100), month: k % 100 })) : undefined;
  const proposal = useMutation({
    mutationFn: (selection: Month[] | undefined) =>
      api<Proposal>('POST', '/api/payments/proposal', { amountUsd, amountEur, costsEur: costs, ...(selection && selection.length > 0 ? { months: selection } : {}) }),
  });
  const resetBank = () => {
    setBank(null);
    setBankRef(null);
    setBankInfo(null);
  };
  const importBank = useMutation({
    mutationFn: (source: { filename?: string; html: string } | { text: string }) =>
      api<BankPrefill>('POST', '/api/payments/bank-import', 'text' in source ? { text: source.text, paidOn } : source),
    // a new import starts clean and a failed one leaves nothing of the previous file attached
    onMutate: () => resetBank(),
    onError: () => resetBank(),
    onSuccess: (r, source) => {
      setPaidOn(r.paidOn);
      setAmountEur(r.amountEur);
      setAmountUsd(r.amountUsd);
      setBankInfo(r);
      setBank('html' in source ? { filename: source.filename ?? 'bank-transaction.html', html: source.html, reference: r.bankReference } : null);
      setBankRef(r.bankReference);
      setSelected(null);
      setAcceptDifference(false);
      proposal.reset();
      proposal.mutate(undefined);
    },
  });
  const [bankRef, setBankRef] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api<Payment>('POST', '/api/payments', {
        paidOn, amountEur, amountUsd, costsEur: costs, months, acceptUsdDifference: acceptDifference,
        ...(bankRef ? { bankReference: bankRef } : {}),
        ...(bank ? { bankDocument: { filename: bank.filename, html: bank.html } } : {}),
      }),
    onSuccess: async () => {
      setBank(null);
      setBankRef(null);
      setBankInfo(null);
      setPasted('');
      setAmountEur('');
      setAmountUsd('');
      setSelected(null);
      setAcceptDifference(false);
      proposal.reset();
      await onCreated();
    },
  });

  const p = proposal.data;
  const chosen = new Set((selected ? months : p?.months)?.map(monthKey) ?? []);
  const toggle = (k: number) => {
    const next = new Set(selected ?? (p?.months.map(monthKey) ?? []));
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setSelected(next);
    // a changed selection gets a new preview
    proposal.mutate([...next].sort((a, b) => a - b).map((x) => ({ year: Math.trunc(x / 100), month: x % 100 })));
  };
  const selectedUsd = p?.candidates.filter((c) => chosen.has(monthKey(c))).reduce((s, c) => s + cents(c.revenueShareUsd), 0) ?? 0;
  const differs = p !== undefined && amountUsd !== '' && Math.abs(selectedUsd - cents(amountUsd)) > chosen.size;

  const propose = (e: FormEvent) => {
    e.preventDefault();
    setSelected(null);
    proposal.mutate(undefined);
  };

  return (
    <div className="max-w-4xl space-y-4 rounded border border-slate-200 p-4 dark:border-slate-800">
      <h2 className="font-semibold">Enter a Steam payment</h2>
      <div className="space-y-2 rounded bg-slate-50 p-3 text-sm dark:bg-slate-900">
        <label className="block">
          <span className="mb-1 block text-slate-600 dark:text-slate-400">Upload bank transaction (Sparkasse HTML, the saved "Umsatzdetails" page of the Valve transfer)</span>
          <input
            type="file"
            accept=".html,.htm,text/html"
            aria-label="Upload bank transaction"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) importBank.mutate({ filename: f.name, html: await f.text() });
            }}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-slate-600 dark:text-slate-400">or paste the Verwendungszweck (the date above is used as the payment date)</span>
          <textarea className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 font-mono text-xs dark:border-slate-700 dark:bg-slate-950" rows={3} value={pasted} onChange={(e) => setPasted(e.target.value)} />
        </label>
        <Button type="button" onClick={() => importBank.mutate({ text: pasted })} disabled={importBank.isPending || pasted.trim() === ''}>Read pasted text</Button>
        {importBank.isError && <ErrorText>{errorText(importBank.error, 'The bank transaction could not be read')}</ErrorText>}
        {bankInfo && (
          <p>
            From the bank transaction {bankInfo.bankReference}: {usd(bankInfo.amountUsd)} sent by Valve
            {bankInfo.rate ? `, rate ${bankInfo.rate}` : ''}, {eur(bankInfo.amountEur)} arrived.
            {bankInfo.intermediaryFeeUsd ? ` ${usd(bankInfo.intermediaryFeeUsd)} intermediary fee, already deducted before conversion; ` : ' '}
            {eur(bankInfo.costsEur)} Sparkasse fee as costs. Check the values and confirm below.
          </p>
        )}
      </div>
      <form className="grid grid-cols-2 gap-3 md:grid-cols-5" onSubmit={propose}>
        <Input label="Paid on" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} required />
        <Input label="EUR received" inputMode="decimal" value={amountEur} onChange={(e) => setAmountEur(e.target.value)} required placeholder="0.00" />
        <Input label="USD (payment notice)" inputMode="decimal" value={amountUsd} onChange={(e) => setAmountUsd(e.target.value)} required placeholder="0.00" />
        <Input label="Costs EUR" inputMode="decimal" value={costs} onChange={(e) => setCostsEur(e.target.value)} required />
        <div className="flex items-end">
          <Button type="submit" disabled={proposal.isPending || !amountEur || !amountUsd}>Propose months</Button>
        </div>
      </form>
      {proposal.isError && <ErrorText>{errorText(proposal.error, 'The proposal failed')}</ErrorText>}
      {p && (
        <div className="space-y-3">
          <p className="text-sm">
            {p.status === 'matched' && !selected
              ? `Proposed: the ${p.months.length} oldest unsettled month(s) whose revenue share matches ${usd(amountUsd)}. Confirm or adjust the selection.`
              : p.status === 'manual' && !selected
                ? `No run of unsettled months matches ${usd(amountUsd)}. Select the covered months.`
                : `Selected ${chosen.size} month(s) with ${usd((selectedUsd / 100).toFixed(2))} revenue share.`}
          </p>
          <table className="text-sm">
            <thead className="text-slate-500">
              <tr><th className="pr-4 text-left">Covered</th><th className="pr-8 text-left">Month</th><th className="pr-8 text-left">Reconciliation</th><th className="text-right">Revenue share</th></tr>
            </thead>
            <tbody>
              {p.candidates.map((c) => {
                const k = monthKey(c);
                return (
                  <tr key={k} className={`border-t border-slate-200 dark:border-slate-800 ${chosen.has(k) ? 'bg-sky-50 dark:bg-sky-950' : ''}`}>
                    <td className="pr-4"><input type="checkbox" aria-label={`Cover ${period(c)}`} checked={chosen.has(k)} onChange={() => toggle(k)} /></td>
                    <td className="pr-8">
                      {period(c)}
                      {c.beforeLastSettled && <span className="ml-2 text-xs text-amber-700 dark:text-amber-400" title="Older than the last settled month; check that it was not paid out before">before the last settled month</span>}
                    </td>
                    <td className={`pr-8 ${RECONCILIATION[c.status].className}`}>{RECONCILIATION[c.status].label}{c.source === 'api' ? ' (Financials API)' : ''}</td>
                    <td className="text-right tabular-nums">{usd(c.revenueShareUsd)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {p.candidates.length === 0 && <p className="text-sm text-slate-500">Every month with a report is settled.</p>}
          {p.blocker && <ErrorText>{p.blocker.message}</ErrorText>}
          {p.preview && !p.blocker && (
            <div>
              <h3 className="font-medium">Drafts</h3>
              <table className="mt-1 text-sm">
                <thead className="text-slate-500">
                  <tr><th className="pr-8 text-left">Partner</th><th className="pr-8 text-right">Partner USD</th><th className="pr-8 text-right">Share</th><th className="text-right">Payout EUR</th></tr>
                </thead>
                <tbody>
                  {p.preview.statements.map((s) => (
                    <tr key={s.partnerId}>
                      <td className="pr-8">{s.partnerName}{s.isHouse ? ' (house)' : ''}</td>
                      <td className="pr-8 text-right tabular-nums">{usd(s.partnerRevenueUsd)}</td>
                      <td className="pr-8 text-right tabular-nums">{Number(s.sharePercent).toFixed(2)}%</td>
                      <td className="text-right tabular-nums">{eur(s.payoutEur)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200 font-medium dark:border-slate-800">
                    <td className="pr-8">Total</td>
                    <td className="pr-8 text-right tabular-nums">{usd(p.preview.totalUsd)}</td>
                    <td className="pr-8" />
                    <td className="text-right tabular-nums">{eur(p.preview.netEur)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-1 text-xs text-slate-500">Net payment = EUR received - {eur(p.preview.costsEur)} costs; the rounding remainder goes to the house partner.</p>
            </div>
          )}
          {differs && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={acceptDifference} onChange={(e) => setAcceptDifference(e.target.checked)} />
              The selected months do not match the USD amount; settle them anyway.
            </label>
          )}
          <Button onClick={() => create.mutate()} disabled={create.isPending || chosen.size === 0 || Boolean(p.blocker) || (differs && !acceptDifference)}>
            Create payment and drafts
          </Button>
          {create.isError && <ErrorText>{errorText(create.error, 'The payment could not be created')}</ErrorText>}
        </div>
      )}
    </div>
  );
}

function PaymentActions({ payment, onChanged }: { payment: Payment; onChanged: () => Promise<unknown> }) {
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const allDrafts = payment.statements.every((s) => s.status === 'draft');
  const remove = useMutation({ mutationFn: () => api('DELETE', `/api/payments/${payment.id}`), onSuccess: onChanged });
  const regenerate = useMutation({ mutationFn: () => api('POST', `/api/payments/${payment.id}/regenerate`), onSuccess: onChanged });
  const reverse = useMutation({
    mutationFn: () => api('POST', `/api/payments/${payment.id}/reverse`, { reason }),
    onSuccess: async () => {
      setReversing(false);
      await onChanged();
    },
  });
  if (payment.reversedAt) return null;
  const error = remove.error ?? regenerate.error ?? reverse.error;
  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-2">
        {allDrafts && <Button onClick={() => regenerate.mutate()} disabled={regenerate.isPending}>Recalculate drafts</Button>}
        {allDrafts && <Button onClick={() => remove.mutate()} disabled={remove.isPending}>Delete payment</Button>}
        {!allDrafts && <Button onClick={() => setReversing(!reversing)}>Reverse payment</Button>}
      </div>
      {reversing && (
        <form className="max-w-2xl space-y-2" onSubmit={(e: FormEvent) => { e.preventDefault(); reverse.mutate(); }}>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Reason for the reversal (drafts are removed, every other statement gets a reversal statement)</span>
            <textarea className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <Button type="submit" disabled={reverse.isPending || reason.trim().length < 10}>Reverse</Button>
        </form>
      )}
      {error && <ErrorText>{errorText(error, 'The change failed')}</ErrorText>}
    </div>
  );
}

export function PaymentsPage() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const payments = useQuery({ queryKey: ['payments'], queryFn: () => api<Payment[]>('GET', '/api/payments') });
  const refresh = () => Promise.all(['payments', 'statements'].map((k) => client.invalidateQueries({ queryKey: [k] })));

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Steam payments</h1>
      <p className="max-w-3xl text-sm text-slate-500">
        A payment settles the revenue share of its covered months. Each partner gets (EUR received - costs) x its share of the covered months' revenue share, its percentage per game valid in each month; the rounding remainder goes to SilentFuture. Months settle once; a mismatch or a missing report blocks them.
      </p>
      {isAdmin && <NewPayment onCreated={refresh} />}
      {payments.isError && <ErrorText>{errorText(payments.error, 'The payments could not be loaded')}</ErrorText>}
      <div className="space-y-3">
        {payments.data?.map((p) => (
          <article key={p.id} className={`rounded border border-slate-200 p-3 dark:border-slate-800 ${p.reversedAt ? 'opacity-60' : ''}`}>
            <header className="flex flex-wrap gap-x-6 text-sm">
              <span className="font-medium">{period(p.months[0]!)}{p.months.length > 1 ? ` - ${period(p.months[p.months.length - 1]!)}` : ''}</span>
              <span>paid {p.paidOn ?? 'unknown'}</span>
              <span>{eur(p.amountEur)} received</span>
              <span>{p.amountUsd ? usd(p.amountUsd) : 'USD not recorded'}</span>
              <span>costs {eur(p.costsEur)}</span>
              {p.legacyPaymentId !== null && <span className="text-slate-500">legacy payment {p.legacyPaymentId}</span>}
              {p.reversedAt && <span className="font-medium text-red-700 dark:text-red-400">reversed {new Date(p.reversedAt).toLocaleDateString('en-GB')}: {p.reversalReason}</span>}
            </header>
            <table className="mt-2 text-sm">
              <tbody>
                {p.statements.map((s) => (
                  <tr key={s.id}>
                    <td className="pr-8"><Link to="/statements/$statementId" params={{ statementId: s.id }} className="underline">{s.partnerName}</Link>{s.reversesStatementId ? ' (reversal)' : ''}</td>
                    <td className={`pr-8 ${STATEMENT_STATUS[s.status].className}`}>{STATEMENT_STATUS[s.status].label}</td>
                    <td className={`pr-8 ${s.deliveryStatus ? DELIVERY_STATUS[s.deliveryStatus].className : ''}`}>{s.deliveryStatus ? DELIVERY_STATUS[s.deliveryStatus].label : ''}</td>
                    <td className="pr-8 text-right tabular-nums">{usd(s.partnerRevenueUsd)}</td>
                    <td className="text-right tabular-nums">{eur(s.payoutEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaymentBookkeeping payment={p} onChanged={refresh} />
            {isAdmin && <PaymentActions payment={p} onChanged={refresh} />}
          </article>
        ))}
        {payments.data?.length === 0 && <p className="text-sm text-slate-500">No payment is recorded yet.</p>}
      </div>
    </section>
  );
}
